import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Message, Scrollbars, Slider } from '@meitu/saas-ui';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { convertImageToOrigin, imageConverter, loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { IImageLayerProps, ILayerProps, IMainLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { handleReportDownloadResult, isFromZhanKu } from '@meitu/xdesign-widgets';
import GenBtn from '@meitu/xdesign-widgets/dist/meidouInfo/compoments/genBtn';
import { DownloadTrackSession } from '@meitu/xdesign-widgets/dist/utils/trackKit';
import { useUserInfoHelper } from '@/editor/header/components/user-info/hooks/use-user-info-helper';
import { events, PLUGIN_CLOSE_EVENT } from '@/events-bus';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { AIExpendImageSource, getAiExpendImageTask, getAiTaskQuery } from '@/services/ai';
import { useEditorModel } from '@/store/models/editor-model';
import { DefaultAIExpendImageInfo, usePluginModel } from '@/store/models/plugin-model';
import { Entrance } from '@/types';
import { getCurrentScene } from '@/utils/track';
import { AiTip } from '../../components/ai-tip/ai-tip';
import BackTitle from '../back-title/back-title';
import aiExpendImageMp4 from './assets/ai-expend-image.mp4';
import mediaIcon from './assets/media.jpg';
import ErrorItem from './error-item';
import useShowLayerWaterMark from './expend-operate/use-show-layer-watermark';
import { adjustImageExpendSize, getImageExpendSize } from './utils';
import './ai-expend-image.less';
import IconFont from '@meitu/xdesign-icon-font';

export const AI_EXPEND_IMAGE = 'image_expand';

const ExpandRadioOptions = () => {
    const { $t } = getLocaleStore();

    return [
        {
            label: $t((v) => v.collapse.moreCollapse.imageTool.aiExpend.originalRadio),
            value: '0',
            icon: 'PictureFill',
        },
        {
            label: '1:1',
            value: '1:1',
            icon: 'Ratio11Bold',
        },
        {
            label: '3:2',
            value: '3:2',
            icon: 'Ratio32Bold',
        },
        {
            label: '2:3',
            value: '2:3',
            icon: 'Ratio23Bold',
        },
        {
            label: '4:3',
            value: '4:3',
            icon: 'Ratio43Bold',
        },
        {
            label: '3:4',
            value: '3:4',
            icon: 'Ratio34Bold',
        },
        {
            label: '16:9',
            value: '16:9',
            icon: 'Ratio169Bold',
        },
        {
            label: '9:16',
            value: '9:16',
            icon: 'Ratio916Bold',
        },
    ];
};

const MultipleList = {
    1: '125%',
    2: '150%',
    3: '200%',
    4: '300%',
};

const MultipleListMap = {
    1: 1.25,
    2: 1.5,
    3: 2,
    4: 3,
} as any;

const MultipleListMapRevert = {
    1.25: 1,
    1.5: 2,
    2: 3,
    3: 4,
} as any;

const AlculatePriceParams = JSON.stringify({
    parameter: {
        rsp_media_type: 'url',
        free_expand_pixel: {
            left: 100,
            right: 100,
            top: 100,
            bottom: 100,
        },
        extra_prompt: '',
        generate_num: 4,
    },
    media_info_list: [
        {
            media_data: mediaIcon,
            media_profiles: {
                media_data_type: 'url',
            },
        },
    ],
});

const AIExpendImage = () => {
    const { $t } = useLocaleStore();
    const aiExpendImageInfo = usePluginModel((state) => state.aiExpendImageInfo);
    const setAIExpendImageInfo = usePluginModel((state) => state.setAIExpendImageInfo);
    const { ratio, desc } = aiExpendImageInfo;
    const [resultList, setResultList] = useState<string[]>([]);
    const [generating, setGenerating] = useState(false);
    const [banGenerate, setBanGenerate] = useState(false);
    const funcUsedMapState = usePluginModel((state) => state.funcUsedMap);
    const setFuncUsedMapAction = usePluginModel((state) => state.setFuncUsedMap);

    const setIsDisableMove = (flag: boolean) => {};

    const setIsDisable = useEditorModel((state) => state.setIsDisable);
    const unit = editor.services.editor.getUnit();
    const setIsAiExpendImage = usePluginModel((state) => state.setIsAiExpendImage);

    const { showUserInfo } = useUserInfoHelper();
    const userInfo = showUserInfo();

    const showLayerWaterMark = useShowLayerWaterMark();

    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(
        editor.services.frame.getActivePickLayer(true) || editor.mainLayer.getMainLayer(),
    );

    useEffect(() => {
        editor.disableOperate();
        return () => {
            editor.enableOperate();
        };
    }, []);

    useEffect(() => {
        if (banGenerate) {
            setTimeout(() => {
                setBanGenerate(false);
            }, 3000);
        }
    }, [banGenerate]);

    useEffect(() => {
        // window._stat_track('tpbj_subfunction_click', {
        //     func: '30_120',
        // });
        window._stat_track('aisc_enter', {
            func: 'image_expand',
        });
        setAIExpendImageInfo(DefaultAIExpendImageInfo());
        setResultList([]);
        setGenerating(false);
        if (!activeLayer) {
            return;
        }
        const { layerType } = activeLayer;

        if (![LayerType.IMAGE_LAYER, LayerType.MAIN_LAYER].includes(layerType)) {
            return;
        }

        setIsAiExpendImage(true);

        setIsDisable(true);

        return () => {
            setIsDisable(false);
        };
    }, []);

    const multipleChange = (value: any) => {
        setAIExpendImageInfo({
            ...aiExpendImageInfo,
            multiple: MultipleListMap[value],
            offsetX: 0,
            offsetY: 0,
            resultUrl: '',
        });
    };

    const selectRadio = (value: string) => {
        setAIExpendImageInfo({
            ...aiExpendImageInfo,
            ratio: value,
            offsetX: 0,
            offsetY: 0,
            resultUrl: '',
        });
    };

    if (!activeLayer) {
        return null;
    }
    const { data, layerType } = activeLayer;

    if (![LayerType.IMAGE_LAYER, LayerType.MAIN_LAYER].includes(layerType)) {
        return null;
    }

    const { url } = data as IImageLayerProps;

    const generateHandle = async () => {
        try {
            console.time('aiExpend time');
            const startDate = new Date();
            window._stat_track('aisc_create_click', {
                func: AI_EXPEND_IMAGE,
                multiple: aiExpendImageInfo.multiple,
                ratio: aiExpendImageInfo.ratio,
                is_creative: !!aiExpendImageInfo.desc,
            });
            window._stat_track('tpbj_subfunction_tick', {
                func: '30_120',
            });
            setGenerating(true);
            setAIExpendImageInfo({
                ...aiExpendImageInfo,
                loading: true,
            });

            const realUrl = convertImageToOrigin(await base64Kit.getRemoteUrlAsync(url));
            const { width: naturalWidth, height: naturalHeight } = await loadImageAsync(realUrl);
            const expendSize = getImageExpendSize(naturalWidth, naturalHeight, aiExpendImageInfo);

            const resultInfo = await adjustImageExpendSize(
                naturalWidth,
                naturalHeight,
                expendSize.newWidth,
                expendSize.newHeight,
                realUrl,
                aiExpendImageInfo,
            );

            const rsp = await getAiExpendImageTask({
                parameter: {
                    rsp_media_type: 'url',
                    free_expand_pixel: {
                        left: resultInfo.left,
                        right: resultInfo.right,
                        top: resultInfo.top,
                        bottom: resultInfo.bottom,
                    },
                    extra_prompt: desc,
                },
                media_info_list: [
                    {
                        media_data: resultInfo.url,
                        media_profiles: {
                            media_data_type: 'url',
                        },
                    },
                ],
            });
            const { msg_id } = rsp.data.data;

            const loopReq = async () => {
                try {
                    const res = await getAiTaskQuery({ msg_id });

                    if (res.data.code === 29901) {
                        setTimeout(() => {
                            loopReq();
                        }, 1000);
                    } else if (res.data.code === 0) {
                        const errorList: string[] = [];
                        if (res.data.data.media_info_list.length < 4) {
                            const img = await loadImageAsync(res.data.data.media_info_list[0].media_data);
                            new Array(4 - res.data.data.media_info_list.length)
                                .fill('')
                                .map((item) => errorList.push(`${img.width}-${img.height}`));
                        }
                        setResultList([
                            ...res.data.data.media_info_list.map((item: any) => item.media_data),
                            ...errorList,
                            ...resultList,
                        ]);
                        setAIExpendImageInfo({
                            ...aiExpendImageInfo,
                            loading: false,
                            resultUrl: res.data.data.media_info_list[0].media_data,
                        });
                        setGenerating(false);
                        window._stat_track('aisc_create_success', {
                            func: AI_EXPEND_IMAGE,
                            multiple: aiExpendImageInfo.multiple,
                            ratio: aiExpendImageInfo.ratio,
                            is_creative: !!aiExpendImageInfo.desc,
                        });
                        const endDate = new Date();
                        window._stat_track('aisc_create_duration', {
                            func: AI_EXPEND_IMAGE,
                            duration: endDate.getTime() - startDate.getTime(),
                        });
                        console.timeEnd('aiExpend time');
                    } else {
                        setAIExpendImageInfo({
                            ...aiExpendImageInfo,
                            loading: false,
                        });
                        setGenerating(false);
                        Message.error(res.data.msg || 'AI扩图失败');
                    }
                } catch (error: any) {
                    setGenerating(false);
                    setAIExpendImageInfo({
                        ...aiExpendImageInfo,
                        loading: false,
                    });
                    Message.error(error.msg || 'AI扩图失败');
                }
            };
            loopReq();
        } catch (error: any) {
            setGenerating(false);
            setAIExpendImageInfo({
                ...aiExpendImageInfo,
                loading: false,
            });
            Message.error(error.msg || $t((v) => v.collapse.moreCollapse.imageTool.messageError.failed));
        }
    };

    const exitClick = () => {
        if (generating) {
            return;
        }
        setAIExpendImageInfo(DefaultAIExpendImageInfo());

        setIsAiExpendImage(false);
        setIsDisableMove(false);

        editor.services.mask.hide();
        events.emit(PLUGIN_CLOSE_EVENT);
    };

    const apply = async (url: string) => {
        if (generating) {
            return;
        }

        setAIExpendImageInfo({
            ...aiExpendImageInfo,
            resultUrl: url,
        });
    };

    const done = async () => {
        try {
            if (!activeLayer) {
                return;
            }
            setIsDisableMove(false);
            const funcUsedMapKey = `aisc_${activeLayer.id}`;
            // Immer 会冻结 Store 快照，应用结果前通过 action 合并使用记录。
            setFuncUsedMapAction({
                ...funcUsedMapState,
                [funcUsedMapKey]: {
                    ...funcUsedMapState[funcUsedMapKey],
                    [AI_EXPEND_IMAGE]: {
                        func: AI_EXPEND_IMAGE,
                        multiple: aiExpendImageInfo.multiple,
                        ratio: aiExpendImageInfo.ratio,
                        is_creative: !!aiExpendImageInfo.desc,
                        url: aiExpendImageInfo.resultUrl,
                    },
                },
            });
            const image = await loadImageAsync(aiExpendImageInfo.resultUrl);

            let isNewLayerSize = true;
            const layers = editor.services.frame.getLayers(editor.services.frame.getActiveFrameId());

            if (!(editor.services.frame.getFrames().length === 1 && layers.length === 2)) {
                isNewLayerSize = false;
            }

            const bgLayer = layers[0];

            if (
                bgLayer.width !== activeLayer.width ||
                bgLayer.height !== activeLayer.height ||
                activeLayer.left !== 0 ||
                activeLayer.top !== 0
            ) {
                isNewLayerSize = false;
            }

            //主图下一定需要进行更新layer
            if (activeLayer.layerType === LayerType.MAIN_LAYER) {
                isNewLayerSize = true;
            }

            const currentStages = editor.services.frame.getFrames();

            const destStages = cloneDeep(currentStages);

            const { width: newWidth, height: newHeight } = image;

            const newData = await editor.imageLayer.processAIExpendImage(
                aiExpendImageInfo.resultUrl,
                activeLayer.id,
                image.width,
                image.height,
                isNewLayerSize ? false : true,
                newWidth,
                newHeight,
                aiExpendImageInfo.ratio,
                '26',
                true,
                undefined,
                isNewLayerSize,
            );
            setActiveLayer(activeLayer);

            if (!(newData && isNewLayerSize)) {
                exitClick();
                return;
            }

            const { newInfo: newLayer, oldInfo: oldLayer } = newData;

            if (activeLayer.layerType === LayerType.MAIN_LAYER) {
                const destSize = {
                    width: newLayer.width,
                    height: newLayer.height,
                };
                const currentSize = {
                    width: activeLayer.width,
                    height: activeLayer.height,
                };

                //手动更新主图中naturalWidth naturalHeight
                const mainLayer = destStages[0].find((item: any) => item.layerType === LayerType.MAIN_LAYER);

                const backgroundLayer = destStages[0].find(
                    (item: any) => item.layerType === LayerType.BACKGROUND_LAYER,
                );
                if (!mainLayer || !backgroundLayer) return;
                (mainLayer.data as IMainLayerProps).naturalWidth = newLayer.width;
                (mainLayer.data as IMainLayerProps).naturalHeight = newLayer.height;
                (mainLayer.data as IMainLayerProps).url = (newLayer.data as IMainLayerProps).url;
                newLayer.rotate = 0;
                Object.assign(mainLayer, newLayer);
                backgroundLayer.width = newLayer.width;
                backgroundLayer.height = newLayer.height;
                backgroundLayer.left = 0;
                backgroundLayer.top = 0;
                editor.services.stage.scale(
                    0,
                    currentStages,
                    destStages,
                    currentSize,
                    destSize,
                    { targetUnit: unit, destUnit: unit },
                    true,
                );
            } else {
                const oldBgLayer = cloneDeep(bgLayer);
                bgLayer.width = newLayer.width;
                bgLayer.height = newLayer.height;
                const newBgLayer = cloneDeep(bgLayer);
                if (newBgLayer) {
                    const newBgLayer = editor.backgroundLayer.generateLayer(bgLayer, bgLayer.data);
                    editor.backgroundLayer.updateAndWrite(newBgLayer, bgLayer);
                }

                if (newBgLayer && oldBgLayer) {
                    editor.imageLayer.expendImageAndChangeBg(newLayer, oldLayer, oldBgLayer, newBgLayer);
                }
            }
            exitClick();
        } catch (error) {
            console.log(error, 'error===');
        }
    };

    const ToastEl = () => {
        return createPortal(
            <div className="operate-toast">
                <IconFont
                    style={{
                        marginRight: 6,
                        color: 'var(--content-system-info-icon-regular, #8CC0FF)',
                    }}
                    name="InfoCircleBoldFill"
                    fontSize={20}
                />
                {$t((v) => v.collapse.moreCollapse.imageTool.aiExpend.toast)}
            </div>,
            document.body,
        );
    };

    const downloadClick = (url: string) => {
        const downloadTrackLocal = new DownloadTrackSession(window._stat_track);
        let success = false;
        try {
            saveAs(
                url,
                `${$t((v) => v.editor.copyRight)}_${
                    isIntl ? 'Zawa' : $t((v) => v.collapse.moreCollapse.imageTool.aiExpend.title)
                }_${dayjs().format('YYYY.MM.DD')}.jpg`,
            );

            window._stat_track('aisc_download_success', {
                func: AI_EXPEND_IMAGE,
                multiple: aiExpendImageInfo.multiple,
                ratio: aiExpendImageInfo.ratio,
                is_creative: !!aiExpendImageInfo.desc,
                download_type: 'direct',
            });
            success = true;

            if (isFromZhanKu()) {
                // 去除查询参数
                const baseUrl = aiExpendImageInfo.resultUrl.split('?')[0];
                // 匹配后缀名
                const match = baseUrl.match(/\.([a-zA-Z0-9]+)$/);
                const extension = match?.[1]?.toLowerCase() || '';
                handleReportDownloadResult(aiExpendImageInfo.resultUrl, {
                    menu: '图像处理',
                    sub_menu: 'AI扩图',
                    extension: extension === 'jpeg' ? 'jpg' : extension,
                });
            }
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            downloadTrackLocal.report({
                resultURL: url,
                success: success ? 1 : 0,
                fileType: 'jpg',
            });
        }
    };
    return (
        <div className="c-aiExpendImage">
            {banGenerate && <ToastEl />}
            <BackTitle
                handleClick={exitClick}
                title={$t((v) => v.collapse.moreCollapse.imageTool.aiExpend.title)}
                iconName="QuestionMarkCircleBold"
                demoUrl={aiExpendImageMp4}
            />

            <div className="c-aiExpendImage-content">
                <Scrollbars>
                    <div className="title">
                        <div>{$t((v) => v.collapse.moreCollapse.imageTool.aiExpend.sizeTitle)}</div>{' '}
                    </div>
                    <div className="slider">
                        <Slider
                            marks={MultipleList}
                            disabled={generating}
                            step={null}
                            onChange={multipleChange}
                            value={MultipleListMapRevert[aiExpendImageInfo.multiple]}
                            min={1}
                            max={4}
                            tooltip={{ open: false }}
                        />
                    </div>

                    <div className="title">{$t((v) => v.collapse.moreCollapse.imageTool.aiExpend.radioTitle)}</div>
                    <div className="ratio-list">
                        {ExpandRadioOptions().map((option) => {
                            return (
                                <div
                                    key={option.value}
                                    onClick={() => {
                                        if (generating) {
                                            return;
                                        }
                                        selectRadio(option.value);
                                    }}
                                    className={classNames({
                                        item: true,
                                        active: option.value === ratio,
                                    })}
                                >
                                    <IconFont name={option.icon} fontSize={14} />
                                    {option.label}
                                </div>
                            );
                        })}
                    </div>

                    {resultList.length > 0 ? (
                        <>
                            <div className="title">{$t((v) => v.collapse.moreCollapse.imageTool.result)}</div>
                            <div className="result-list">
                                {resultList.map((item) => {
                                    if (!item.includes('http')) {
                                        return (
                                            <ErrorItem
                                                key={item}
                                                width={item.split('-')[0]}
                                                height={item.split('-')[1]}
                                                text={$t((v) => v.collapse.moreCollapse.imageTool.aiExpend.expend)}
                                            />
                                        );
                                    }
                                    return (
                                        <div className="img-wrap" key={item}>
                                            <div className="download-result-btn" onClick={() => downloadClick(item)}>
                                                <IconFont fontSize={12} name="DownloadBold" color="#fff" />
                                            </div>

                                            <img
                                                onClick={() => apply(item)}
                                                className={classNames({
                                                    item: 'true',
                                                    active: item === aiExpendImageInfo.resultUrl,
                                                })}
                                                src={imageConverter(item, 360)}
                                                alt={$t((v) => v.collapse.moreCollapse.imageTool.image)}
                                                key={item}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : null}
                </Scrollbars>
            </div>

            <div className="operate">
                <div className="operate-btn">
                    <div
                        className={classNames({
                            exit: true,
                            disable: generating,
                        })}
                        onClick={() => {
                            aiExpendImageInfo.resultUrl ? done() : exitClick();
                        }}
                    >
                        {aiExpendImageInfo.resultUrl
                            ? $t((v) => v.collapse.moreCollapse.imageTool.apply)
                            : $t((v) => v.collapse.moreCollapse.imageTool.exit)}
                    </div>
                    <GenBtn
                        hideDesc={(generating && isIntl) || (isIntl && !!resultList.length)}
                        uuid={AI_EXPEND_IMAGE}
                        text={
                            generating ? (
                                <div className="plugin-aiText-btns-loading">
                                    <IconFont fontSize={14} name={'LoadingCircleBlack'} />
                                    <div style={{ marginLeft: 4 }}>
                                        {$t((v) => v.collapse.moreCollapse.imageTool.generating)}
                                    </div>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {resultList.length > 0
                                        ? $t((v) => v.collapse.moreCollapse.imageTool.reGenerate)
                                        : $t((v) => v.collapse.moreCollapse.imageTool.imGenerate)}
                                </div>
                            )
                        }
                        style={{
                            width: 246,
                            opacity: generating || banGenerate ? 0.6 : 1,
                        }}
                        disabled={generating || banGenerate}
                        onGen={generateHandle}
                        onErrorMessage={(msg: string) => {
                            Message.error(msg);
                        }}
                        source={getCurrentScene()}
                        second_source="image_expand"
                    />
                </div>

                <AiTip />
            </div>
        </div>
    );
};

export default AIExpendImage;
